import React from 'react';
import { Calendar, Views } from 'react-big-calendar';
import { addDays, format, isBefore, isSameMonth, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import classNames from 'classnames';
import { calendarLocalizer, calendarMessages } from '../../../../utils/calendarUtils';
import { RoutineCalendarEvent } from '../../../../components/RoutineCalendarEvent/RoutineCalendarEvent';
import { CalendarToolbar } from '../../../../components/CalendarToolbar/CalendarToolbar';
import { createRoutineSummaryItems } from '../../../../utils/calendarToolbarUtils';
import { useProfileClient } from '../hooks/useProfileClient';
import { useProfileRoutinePanel } from '../hooks/useProfileRoutinePanel';
import { useRoutinesCalendar } from '../hooks/useRoutinesCalendar';
import {
  dayPropGetter,
  getCalendarTimeBounds,
  getRoutineEventColor,
  getRoutineEventDescription,
  getRoutineEventIcon,
} from '../utils';
import { RoutineHistoryItem } from '../types';
import { ActivityList } from '../../../../components/ActivityTimeline/ActivityList';
import { ActivityView, ActivityViewToggle } from '../../../../components/ActivityTimeline/ActivityViewToggle';
import { dataFillProps } from '../../../../utils/dataFill';

export const RoutinesTab = ({
  calendar: routinesCalendar,
  user,
  handleViewHistoryRoutine,
  view,
  onViewChange,
}: {
  calendar: ReturnType<typeof useRoutinesCalendar>;
  user: ReturnType<typeof useProfileClient>['user'];
  handleViewHistoryRoutine: ReturnType<typeof useProfileRoutinePanel>['handleViewHistoryRoutine'];
  view: ActivityView;
  onViewChange: (view: ActivityView) => void;
}) => {
  const { minTime, maxTime } = getCalendarTimeBounds();

  const renderCurrentRoutineSummaryItem = () => {
    return (
      <>
        <i
          className={classNames('pi', {
            'pi-check-circle': user?.currentRoutine,
            'pi-times-circle': !user?.currentRoutine,
            success: user?.currentRoutine,
            error: !user?.currentRoutine,
          })}
        />
        <span>{user?.currentRoutine ? 'Rutina activa' : 'Sin rutina'}</span>
      </>
    );
  };

  // Empty while the month loads.
  const history = routinesCalendar.routineHistory ?? [];
  const summaryItems = createRoutineSummaryItems(history.map((item) => ({ eventType: item.eventType })));

  const openEvent = (
    event: Pick<RoutineHistoryItem, 'eventType' | 'routineId' | 'fieldsUpdated'> & { id: string; start: Date; title: string },
  ) =>
    handleViewHistoryRoutine({
      id: event.id,
      date: event.start.toISOString(),
      description: event.title,
      icon: getRoutineEventIcon(event.eventType),
      eventType: event.eventType,
      routineId: event.routineId,
      fieldsUpdated: event.fieldsUpdated,
    });

  const toggle = (
    <div className='activity-view-bar'>
      <ActivityViewToggle name='routines-view' value={view} onChange={onViewChange} />
    </div>
  );

  if (view === 'list') {
    // The answer covers the month grid (with days of the neighbouring months); the list shows the month only. A routine
    // that started before the month and still runs in it is in the month too: it is listed on the first day of the month.
    const monthStart = startOfMonth(routinesCalendar.routinesCalendarDate);
    const startedBefore = (item: { date: string }) => isBefore(new Date(item.date), monthStart);
    const monthHistory = history.filter((item) => {
      const start = new Date(item.date);
      if (isSameMonth(start, monthStart)) return true;
      // The same end as the calendar event below.
      return startedBefore(item) && addDays(start, item.duration ? item.duration + 1 : 0) > monthStart;
    });
    const items = monthHistory.map((item, index) => {
      const start = new Date(item.date);
      const title = getRoutineEventDescription(item.eventType, item.fieldsUpdated);
      return {
        id: `${item.routineId}-${index}`,
        start: startedBefore(item) ? monthStart : start,
        title,
        color: getRoutineEventColor(item.eventType),
        details: [
          ...(startedBefore(item) ? [`Desde el ${format(start, "d 'de' MMMM", { locale: es })}`] : []),
          ...(item.duration ? [`${item.duration} ${item.duration === 1 ? 'día' : 'días'}`] : []),
        ],
        onSelect: () =>
          openEvent({
            id: item.routineId,
            start,
            title,
            eventType: item.eventType,
            routineId: item.routineId,
            fieldsUpdated: item.fieldsUpdated,
          }),
      };
    });
    return (
      <>
        {toggle}
        <ActivityList
          date={routinesCalendar.routinesCalendarDate}
          onNavigate={routinesCalendar.handleRoutinesCalendarNavigate}
          items={items}
          summaryItems={createRoutineSummaryItems(monthHistory.map((item) => ({ eventType: item.eventType })))}
          customSummaryItems={[renderCurrentRoutineSummaryItem()]}
        />
      </>
    );
  }

  return (
    <>
      {toggle}
      <div className='calendar-container'>
        <Calendar
          dayLayoutAlgorithm='no-overlap'
          localizer={calendarLocalizer}
          events={history.map((item) => {
            const startDate = new Date(item.date);
            // Have to add one day so that it shows as inclusive in react big calendar
            const endDate = addDays(startDate, item.duration ? item.duration + 1 : 0);
            return {
              id: item.routineId,
              title: getRoutineEventDescription(item.eventType),
              start: startDate,
              end: endDate,
              routineId: item.routineId,
              eventType: item.eventType,
            };
          })}
          startAccessor='start'
          endAccessor='end'
          step={30}
          timeslots={1}
          messages={calendarMessages}
          views={[Views.MONTH]}
          view={Views.MONTH}
          date={routinesCalendar.routinesCalendarDate}
          onNavigate={routinesCalendar.handleRoutinesCalendarNavigate}
          onView={() => {}}
          max={maxTime}
          min={minTime}
          scrollToTime={(() => {
            const adjustedTime = new Date(new Date().setHours(new Date().getHours() - 1, 0, 0, 0));
            return adjustedTime < minTime ? minTime : adjustedTime;
          })()}
          eventPropGetter={(event) => {
            const color = getRoutineEventColor(event.eventType);
            return dataFillProps(color);
          }}
          components={{
            event: (props) => <RoutineCalendarEvent {...props} />,
            toolbar: (props) => (
              <CalendarToolbar {...props} summaryItems={summaryItems} customSummaryItems={[renderCurrentRoutineSummaryItem()]} />
            ),
            showMore: ({ count }) => (
              <div className='rbc-show-more' style={{ cursor: 'default' }}>
                +{count} más
              </div>
            ),
          }}
          onSelectEvent={(event: any) => openEvent(event)}
          dayPropGetter={dayPropGetter}
        />
      </div>
    </>
  );
};
